import React, {memo, useCallback, useEffect, useId, useMemo, useRef, useState} from 'react';
import {useRouter} from 'next/router';
import Link from 'next/link';
import {MIN_SEARCH_LENGTH, searchNavLinks} from '@/util/menu-links/searchIndex';
import {useNavSearchContent} from '@/hooks/useNavSearchContent';
import Icon from '@/components/atoms/Icon';
import styles from './NavSearch.module.scss';

// ─── Nav search ───────────────────────────────────────────────────────────────
// Two presentations of one behaviour, chosen by the `compact` prop:
//
//   compact={false} → the inline pill input shown in the desktop nav row.
//   compact         → an icon only; tapping it opens the input as a full-width row
//                     directly under the toolbar (the small-screen layout has no
//                     horizontal room for a permanent field).
//
// Matching runs against the nav's own link index (src/util/menu-links/searchIndex),
// so results are always exactly the pages the dropdown can reach — no separate
// search backend, no request per keystroke.

const PLACEHOLDER = 'What would you like to build?';

const cx = (...classes: (string | false | undefined)[]) => classes.filter(Boolean).join(' ');

// Widths (styles/breakpoints.ts): the inline field shows from NAV_INLINE_SEARCH_MIN (1350px);
// below it the search collapses to its icon. Styles live in NavSearch.module.scss.

interface NavSearchProps {
    /** Icon-only trigger that opens a full-width row under the toolbar. */
    compact?: boolean;
    /**
     * Compact trigger rendered inside the *desktop* nav row — visible only in the
     * 1200–1349px band, where the nav items show but the inline field does not fit.
     */
    inDesktopRow?: boolean;
}

const NavSearch: React.FC<NavSearchProps> = ({compact = false, inDesktopRow = false}) => {
    const router = useRouter();
    const [query, setQuery] = useState('');
    const [isOpen, setIsOpen] = useState(false); // compact only: is the row expanded
    const [showResults, setShowResults] = useState(false);
    // Index into `navigable` of the arrow-key highlighted row; -1 = nothing highlighted.
    const [activeIndex, setActiveIndex] = useState(-1);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    // Stable per-instance id prefix — three NavSearch instances are mounted at once
    // (mobile, inline desktop, collapsed desktop), so the listbox/option ids they emit
    // must not collide.
    const baseId = useId();
    const listboxId = `${baseId}-listbox`;
    const optionId = (index: number) => `${baseId}-option-${index}`;

    // Section 1 — service/product pages, matched against the static nav index.
    const results = useMemo(() => searchNavLinks(query), [query]);
    // Sections 2 & 3 — 4 most recently updated use cases and blog posts, narrowed by the
    // term once there is one. Only fetched while the panel is actually on screen.
    const {useCases, blogs, loading: contentLoading} = useNavSearchContent(query, showResults);

    const hasServices = query.trim().length >= MIN_SEARCH_LENGTH && results.length > 0;

    // Every row the user can reach, in render order. This is the single source of truth
    // for both arrow-key movement and what Enter activates — previously Enter re-derived
    // its own "first match" precedence, which could disagree with what was on screen.
    const navigable = useMemo(
        () => [
            ...(hasServices ? results.map((entry) => ({path: entry.path, target: entry.target})) : []),
            ...blogs.map((item) => ({path: item.path, target: undefined as string | undefined})),
            ...useCases.map((item) => ({path: item.path, target: undefined as string | undefined}))
        ],
        [hasServices, results, blogs, useCases]
    );

    const reset = useCallback(() => {
        setQuery('');
        setShowResults(false);
        setIsOpen(false);
        setActiveIndex(-1);
    }, []);

    // The highlighted row must not survive a change to the list under it — otherwise
    // index 5 of the old results silently becomes index 5 of the new ones.
    useEffect(() => {
        setActiveIndex(-1);
    }, [navigable]);

    // Focus the field as soon as the compact row opens — the tap that opened it was
    // clearly an intent to type.
    useEffect(() => {
        if (isOpen) inputRef.current?.focus();
    }, [isOpen]);

    // Any click outside dismisses the results (and, when compact, the whole row).
    useEffect(() => {
        if (!showResults && !isOpen) return undefined;
        const onPointerDown = (event: MouseEvent) => {
            if (!wrapperRef.current?.contains(event.target as Node)) {
                setShowResults(false);
                if (compact) setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', onPointerDown);
        return () => document.removeEventListener('mousedown', onPointerDown);
    }, [showResults, isOpen, compact]);

    // A completed navigation always closes the search, however it was triggered.
    useEffect(() => {
        router.events.on('routeChangeComplete', reset);
        return () => router.events.off('routeChangeComplete', reset);
    }, [router.events, reset]);

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setQuery(event.target.value);
        setShowResults(true);
    };

    const go = useCallback(
        (destination: {path: string; target?: string}) => {
            if (destination.target === '_blank') globalThis.open(destination.path, '_blank', 'noopener,noreferrer');
            else router.push(destination.path);
            reset();
        },
        [router, reset]
    );

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Escape') {
            reset();
            return;
        }

        // Arrow keys walk the rendered rows, wrapping at both ends.
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            if (navigable.length === 0) return;
            event.preventDefault();
            setShowResults(true);
            const step = event.key === 'ArrowDown' ? 1 : -1;
            setActiveIndex((current) => (current + step + navigable.length) % navigable.length);
            return;
        }

        // Enter follows the highlighted row, or — when the user has typed and gone straight
        // for Enter without arrowing — the first one. Section order decides what "first"
        // means: a service page, else a blog, else a use case.
        if (event.key === 'Enter') {
            const destination = navigable[activeIndex] ?? navigable[0];
            if (!destination) return;
            event.preventDefault();
            go(destination);
        }
    };

    // `index` is the row's position in `navigable`; -1 marks a row that is rendered but not
    // reachable by keyboard (there are none today, but it keeps the signature honest).
    const renderRow = (key: string, index: number, path: string, label: string, target?: string) => (
        <Link
            key={key}
            href={path}
            target={target || undefined}
            rel={target === '_blank' ? 'noopener noreferrer' : undefined}
            id={optionId(index)}
            role="option"
            aria-selected={index === activeIndex}
            onMouseEnter={() => setActiveIndex(index)}
            onClick={reset}
        >
            <div className={cx(styles.resultRow, index === activeIndex && styles.resultRowActive)}>
                <span className={`font-regular-text nav-search-result-label ${styles.resultLabel}`}>{label}</span>
            </div>
        </Link>
    );

    // The three sections always render in the same order — services, then blogs, then use
    // cases — so the panel's shape is predictable as the user types. An empty section is
    // dropped rather than shown with a "nothing here" line, and the first section rendered
    // never carries the divider rule (whichever one it turns out to be).
    const renderResults = (listClassName: string) => {
        if (!showResults) return null;

        const isEmpty = !hasServices && useCases.length === 0 && blogs.length === 0;
        const titleClassFor = (isFirst: boolean) => cx('font-headers', styles.sectionTitle, !isFirst && styles.sectionTitleDivided);
        // Running offset into `navigable`, advanced section by section so each row's
        // keyboard index matches its render position.
        const serviceCount = hasServices ? results.length : 0;

        return (
            <div className={listClassName} id={listboxId} role="listbox" aria-label="Search results" data-lenis-prevent>
                {hasServices && (
                    <>
                        <p className={titleClassFor(true)}>Services</p>
                        {results.map((entry, index) => renderRow(entry.path, index, entry.path, entry.label, entry.target))}
                    </>
                )}

                {blogs.length > 0 && (
                    <>
                        <p className={titleClassFor(!hasServices)}>Blogs / Insights</p>
                        {blogs.map((item, index) => renderRow(`blog-${item.id}`, serviceCount + index, item.path, item.label))}
                    </>
                )}

                {useCases.length > 0 && (
                    <>
                        <p className={titleClassFor(!hasServices && blogs.length === 0)}>Use Cases</p>
                        {useCases.map((item, index) =>
                            renderRow(`uc-${item.id}`, serviceCount + blogs.length + index, item.path, item.label)
                        )}
                    </>
                )}

                {isEmpty && (
                    <p className={`font-regular-text ${styles.emptyState}`}>{contentLoading ? 'Searching…' : 'No matching pages'}</p>
                )}
            </div>
        );
    };

    const field = (
        <div className={styles.field}>
            <Icon name="Search" fontSize="inherit" className={styles.searchIcon} />
            <div className={styles.inputRoot}>
                <input
                    ref={inputRef}
                    className={styles.input}
                    value={query}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                    onFocus={() => setShowResults(true)}
                    placeholder={PLACEHOLDER}
                    type="text"
                    aria-label="Search the site"
                    // Combobox pattern: the input owns the listbox and reports which row is
                    // highlighted, so a screen reader announces rows as the user arrows.
                    role="combobox"
                    aria-expanded={showResults}
                    aria-controls={listboxId}
                    aria-autocomplete="list"
                    aria-activedescendant={activeIndex >= 0 ? optionId(activeIndex) : undefined}
                    autoComplete="off"
                />
            </div>
        </div>
    );

    if (compact) {
        return (
            <div ref={wrapperRef} className={inDesktopRow ? styles.desktopCollapsed : undefined}>
                <button
                    type="button"
                    className={styles.toggleButton}
                    aria-label={isOpen ? 'Close search' : 'Open search'}
                    aria-expanded={isOpen}
                    onClick={() => (isOpen ? reset() : setIsOpen(true))}
                >
                    <Icon name={isOpen ? 'Close' : 'Search'} fontSize="inherit" className={styles.toggleIcon} />
                </button>

                {isOpen && (
                    <div className={styles.compactPanel}>
                        {field}
                        {renderResults(styles.resultsList)}
                    </div>
                )}
            </div>
        );
    }

    return (
        <div ref={wrapperRef} className={styles.inlineWrapper}>
            {field}
            {renderResults(`${styles.resultsList} ${styles.inlineResults}`)}
        </div>
    );
};

export default memo(NavSearch);
